/* eslint-disable no-template-curly-in-string */
// The yup message does its own interpolation. So
// the normal qoutes with ${label} inside them is
// actually correct and does not use back ticks

// import * as yup from 'yup';
// Note require is used here instead of the above commented out code
// because we want to add yup._id() etc, which works with CommonJS
// but not with ES6 (named) module exports. This is OK because we are
// ultimately exporting the whole "yup object" and not named exports.
const yup = require('yup');
const { isTemplateCode, makeTemplateCode } = require('src/lib');

yup.addMethod(yup.mixed, 'requiredWhen', function (_requires = []) {
  const requires = Array.isArray(_requires) ? _requires : [_requires];
  return this.test({
    name: 'requiredWhen',
    exclusive: true,
    message: '${label} is a required field',
    test: function (value) {
      const { context } = this.options;
      if (!context || !context.method) {
        return true;
        // throw new Error(
        //   `The "requiredWhen" validation method can only be used when "validate" is call with options.context.method`
        // );
      }
      if (requires.includes(context.method)) {
        return typeof value !== 'undefined';
      } else {
        // This field is not required on this method
        return true;
      }
    }
  });
});

yup.trimmedString = () =>
  yup.string().typeError('${label} must be a string').trim();

yup._id = () => yup.trimmedString().matches(/^[a-fA-F0-9]{24}$/);

yup.estimate = () => yup.number().positive();

yup.addMethod(yup.string, 'notEmpty', function () {
  return this.test({
    name: 'notEmpty',
    exclusive: true,
    message: '${label} cannot by empty',
    test: (value) => {
      if (value === undefined || value === null) {
        // this does not check undefined or "required",
        // only that a string has length
        return true;
      }
      return value.length > 0;
    }
  });
});

yup.addMethod(yup.string, 'defaultNullable', function () {
  return this.nullable().default(null);
  // .transform(function(value, originalvalue) {
  //   return typeof value === 'string' && value.length === 0 ? null : value;
  // });
});

yup.addMethod(yup.string, 'phone', function (message) {
  return this.test({
    name: 'phone',
    exclusive: true,
    message: message || '${label} must be a 10 digit phone number',
    test: (value) => {
      return value
        ? value.length === 10 && new RegExp(/^\d+$/).test(value)
        : true;
    }
  });
});

yup.addMethod(yup.string, 'password', function (message) {
  return this.test({
    name: 'password',
    exclusive: true,
    message:
      message ||
      '${label} must be 8+ characters, 1 lowercase letter, 1 UPPERCASE letter, 1 number, 1 special character',
    test: (value) => {
      return value
        ? new RegExp(/^((?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[\W]).{8,})/).test(
            value
          )
        : true;
    }
  });
});

yup.addMethod(yup.mixed, 'isNotEqual', function (ref, message) {
  return this.test({
    name: 'isNotEqual',
    exclusive: true,
    params: {
      ref
    },
    message: message || '${label} cannot be the same ${ref}',
    test: function (value) {
      return value ? value !== this.resolve(yup.ref(ref)) : true;
    }
  });
});

yup.addMethod(yup.mixed, 'isEqual', function (ref, message) {
  return this.test({
    name: 'isEqual',
    exclusive: true,
    params: {
      ref
    },
    message: message || '${label} must be the same as ${ref}',
    test: function (value) {
      return value ? value === this.resolve(yup.ref(ref)) : true;
    }
  });
});

yup.addMethod(yup.date, 'before', function (ref, message) {
  return this.test({
    name: 'before',
    exclusive: true,
    params: {
      ref
    },
    message: message || '${label} must be before ${ref}',
    test: function (value) {
      return value ? value < this.resolve(yup.ref(ref)) : true;
    }
  });
});

yup.addMethod(yup.date, 'after', function (ref, message) {
  return this.test({
    name: 'after',
    exclusive: true,
    message: message || '${label} must be after ${ref}',
    test: function (value) {
      return value ? value > this.resolve(yup.ref(ref)) : true;
    }
  });
});

yup.addMethod(yup.number, 'length', function (length, message) {
  return this.test({
    name: 'length',
    exclusive: true,
    params: {
      length
    },
    message: message || '${label} must be exactly ${length} numbers',
    test: function (value) {
      return value ? value.toString().length === length : true;
    }
  });
});

yup.addMethod(yup.number, 'lengthMin', function (length, message) {
  return this.test({
    name: 'lengthMin',
    exclusive: true,
    params: {
      length
    },
    message: message || '${label} must be at least ${length} numbers',
    test: function (value) {
      return value ? value.toString().length >= length : true;
    }
  });
});

yup.addMethod(yup.number, 'lengthMax', function (length, message) {
  return this.test({
    name: 'lengthMax',
    exclusive: true,
    params: {
      length
    },
    message: message || '${label} must be no more than ${length} numbers',
    test: function (value) {
      return value ? value.toString().length <= length : true;
    }
  });
});

yup.addMethod(yup.string, 'numbersOnly', function (message) {
  return this.test({
    name: 'numbersOnly',
    exclusive: true,
    message: message || '${label} must contain only numbers',
    test: function (value) {
      const numbersOnly = RegExp(/^[0-9]*$/);
      return value ? numbersOnly.test(value) : true;
    }
  });
});

// Template codes must be a valid value or a string like ${...}
// containing a valid template_code_id
yup.templateCode = (valueSchema) =>
  yup.lazy((value) => {
    if (isTemplateCode(value)) {
      return templateCodeSchema;
    }

    return valueSchema.nullable().default(null);
  });

const templateCodeSchema = yup.string().test({
  name: 'templateCode',
  exclusive: true,
  message:
    '${label} must be a valid value or a template code like ${my_template_code_id}',
  test: function (value) {
    const template_code_id = makeTemplateCode(value);
    // TODO: Check this is a valid id in the templates codes table
    return template_code_id.match(/^[a-fA-F0-9]{24}$/);
  }
});

const stageTemplateSchemas = {
  url: {
    url: yup.templateCode(yup.trimmedString().url().label('URL'))
  },
  milestone: {
    question_group_id: yup.templateCode(yup._id().label('Question Group Id'))
  },
  travel: {
    origin_address: yup.templateCode(
      yup.trimmedString().label('Origin Address')
    ),
    origin_suite: yup.templateCode(yup.trimmedString().label('Origin Suite')),
    destination_address: yup.templateCode(
      yup.trimmedString().label('Destination Address')
    ),
    destination_suite: yup.templateCode(
      yup.trimmedString().label('Destination Suite')
    )
  },
  work: {}
};

// Validate stages-templates by their proper type
yup.stageTemplate = () => {
  return yup.lazy((stageTemplate) => {
    return yup
      .object()
      .shape(
        {
          title: yup.trimmedString().required().label('Title'),
          type: yup
            .trimmedString()
            .required()
            .oneOf(['milestone', 'work', 'travel', 'url'])
            .label('Type'),
          original_estimate: yup.templateCode(
            yup.estimate().label('Original Estimate')
          ),
          billing_rate: yup.templateCode(
            yup
              .number()
              .positive()
              .nullable()
              .when('billing_type', (billing_type, schema) => {
                return billing_type
                  ? schema.required(
                      'Billing Rate is required when Billing Type is set.'
                    )
                  : schema;
              })
              .label('Billing Rate')
          ),
          billing_type: yup.templateCode(
            yup
              .trimmedString()
              .oneOf(['flat', 'hourly', null])
              .nullable()
              .when('billing_rate', (billing_rate, schema) => {
                return billing_rate
                  ? schema.required(
                      'Billing Type is required when Billing Rate is set.'
                    )
                  : schema;
              })
              .label('Billing Type')
          ),
          cost_rate: yup.templateCode(
            yup
              .number()
              .positive()
              .nullable()
              .when('cost_type', (cost_type, schema) => {
                return cost_type
                  ? schema.required(
                      'Cost Rate is required when Cost Type is set.'
                    )
                  : schema;
              })
              .label('Cost Rate')
          ),
          cost_type: yup.templateCode(
            yup
              .trimmedString()
              .oneOf(['flat', 'hourly', null])
              .nullable()
              .when('cost_rate', (cost_rate, schema) => {
                return cost_rate
                  ? schema.required(
                      'Cost Type is required when Cost Rate is set.'
                    )
                  : schema;
              })
              .label('Cost Type')
          )
        },
        [
          ['billing_rate', 'billing_type'],
          ['cost_rate', 'cost_type']
        ]
      )
      .shape(stageTemplateSchemas[stageTemplate.type]);
  });
};

const stageSchemas = {
  url: {
    original_estimate: yup.estimate().required().label('Original Estimate'),
    url: yup.trimmedString().url().label('URL').required()
  },
  milestone: {
    original_estimate: yup.estimate().required().label('Original Estimate'),
    question_group_id: yup._id().label('Question Group Id').required(),
    questions: yup
      .array()
      .of(
        yup.object({
          label: yup.trimmedString().label('Label').required(),
          type: yup
            .trimmedString()
            .label('Type')
            .required()
            .oneOf(['text', 'textarea', 'checkbox', 'job_document_id']),
          answer: yup.mixed().label('Answer')
        })
      )
      .label('Questions')
      .requiredWhen(['update'])
  },
  travel: {
    // Note original_estimate is not required here,
    // it is calculated on creation
    original_estimate: yup.estimate().nullable().label('Original Estimate'),
    origin_address: yup
      .trimmedString()
      .isNotEqual(
        'destination_address',
        'Origin and Destination addresses cannot be the same'
      )
      .label('Origin Address')
      .required(),
    origin_suite: yup.trimmedString().nullable().label('Origin Suite'),
    destination_address: yup
      .trimmedString()
      .isNotEqual(
        'origin_address',
        'Origin and Destination addresses cannot be the same'
      )
      .label('Destination Location')
      .required(),
    destination_suite: yup.trimmedString().nullable().label('Destination Suite')
  },
  work: {
    original_estimate: yup.estimate().required().label('Original Estimate')
  }
};

yup.stage = () => {
  return yup.lazy((stage) => {
    return yup
      .object()
      .shape(
        {
          title: yup.trimmedString().required().label('Title'),
          type: yup
            .trimmedString()
            .required()
            .oneOf(['milestone', 'work', 'travel', 'url'])
            .label('Type'),
          billing_rate: yup // flat:pennies, hourly: pennies/hr
            .number()
            .positive()
            .nullable()
            .when('billing_type', (billing_type, schema) => {
              return billing_type
                ? schema.required(
                    'Billing Rate is required when Billing Type is set.'
                  )
                : schema;
            })
            .label('Billing Rate'),
          billing_type: yup
            .trimmedString()
            .oneOf(['flat', 'hourly', null])
            .nullable()
            .when('billing_rate', (billing_rate, schema) => {
              return billing_rate
                ? schema.required(
                    'Billing Type is required when Billing Rate is set.'
                  )
                : schema;
            })
            .label('Billing Type'),
          cost_rate: yup // flat:pennies, hourly: pennies/hr
            .number()
            .positive()
            .nullable()
            .when('cost_type', (cost_type, schema) => {
              return cost_type
                ? schema.required(
                    'Cost Rate is required when Cost Type is set.'
                  )
                : schema;
            })
            .label('Cost Rate'),
          cost_type: yup
            .trimmedString()
            .oneOf(['flat', 'hourly', null])
            .nullable()
            .when('cost_rate', (cost_rate, schema) => {
              return cost_rate
                ? schema.required(
                    'Cost Type is required when Cost Rate is set.'
                  )
                : schema;
            })
            .label('Cost Type')
        },
        [
          ['billing_rate', 'billing_type'],
          ['cost_rate', 'cost_type']
        ]
      )
      .shape(stageSchemas[stage.type]);
  });
};

export default yup;
